import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter, Subscription } from 'rxjs';
import { AuthServiceService } from 'src/app/services/auth-service/auth-service.service';
import { environment } from 'src/environments/environment';
import { oktaAuth } from 'src/app/config/okta-config';
import { ApigeeAuthService } from 'src/app/services/apigee-service/apigee-service';

@Component({
  selector: 'app-nav-bar',
  standalone: true,
  imports: [CommonModule, RouterLink],
  providers:[ApigeeAuthService],
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss']
})
export class NavBarComponent implements OnInit {
  currentRoute: string = '';
  showDropdown: boolean = false;
  showDropdownMasterData: boolean = false;
  showDropdownAdministrativeRule: boolean = false;
  selectedUserType: string = 'outbound';

  isDashboardSelect: boolean = false;
  isControlCenterSelect: boolean = false;
  isMasterDataSelects: boolean = false;
  isAdministratorRuleSelect: boolean = false
  isReportSelect: boolean = false;
  userAccessLevel: string = '';

  profileDropdownVisible = false;
  userName = '';
  userNameSubscribed!: Subscription;

  constructor(public router: Router,
     public route: ActivatedRoute,
      public eRef: ElementRef, 
      public authService: AuthServiceService,
       public apigeeAuthService:ApigeeAuthService,) { }

  isDashboardRoute: boolean = false;
  isExternalDashboardRoute: boolean = false;
  isConsumRoute: boolean = false;
  isLoginRoute: boolean = false;
  isCheckEmailRoute: boolean = false;

  ngOnInit() {
    const savedUserType = localStorage.getItem('selectedUserType');
    this.selectedUserType = savedUserType ?? 'outbound';
    //this.userName = this.authService.username;
    this.userNameSubscribed = this.authService.username$.subscribe(name => {
      this.userName = name;
    })

    this.authService.loadUserGroups();
    this.userAccessLevel = this.authService.getAccessLevel();


    this.route.queryParams.subscribe(params => {
      if (params['userSelectedType']) {
        this.selectedUserType = params['userSelectedType'];
        localStorage.setItem('selectedUserType', this.selectedUserType);
      }
    });

    this.currentRoute = this.router.url;
    this.isDashboardRoute = this.currentRoute.includes('/dashboard');
    this.isExternalDashboardRoute = this.currentRoute.includes('/extdashboard');
    this.isConsumRoute = this.currentRoute.includes('/consum-epcis');
    this.isLoginRoute = this.currentRoute.includes('/login');
    this.isCheckEmailRoute = this.currentRoute.includes('/checkemail');
    this.highlightActiveLink();
    // Detect active route on navigation
    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.currentRoute = event.url;
        this.isDashboardRoute = this.currentRoute.includes('/dashboard');
        this.isExternalDashboardRoute = this.currentRoute.includes('/extdashboard');
        this.isConsumRoute = this.currentRoute.includes('/consum-epcis');
        this.isCheckEmailRoute = this.currentRoute.includes('/checkemail');
        this.isLoginRoute = this.currentRoute.includes('/login');
        this.highlightActiveLink();
      });

    this.highlightActiveLink();
  }

  canShowAdminRule() {
    const userType = localStorage.getItem('userType');
    if (userType === 'external') {
      return false;
    }
    return ['admin', 'inboundAdmin', 'outboundAdmin'].includes(this.userAccessLevel);
  }

  canShowControlCenter(): boolean {
    const userType = localStorage.getItem('userType');
    if (userType === 'external') {
      // External users only get inbound control center
      return this.currentRoute.includes('/control-center-inbound');
    }
    return ['admin', 'inboundAdmin', 'outboundAdmin', 'inboundUser', 'outboundUser'].includes(this.userAccessLevel);
  }


  canShowMasterData(): boolean {
    const userType = localStorage.getItem('userType');
    if (userType === 'external') {
      return false;
    }
    // Allow global admin
    if (this.userAccessLevel === 'admin') return true;

    // Also consider raw Okta groups in case the computed accessLevel picks one admin over another
    const groups = this.authService.getGroups?.() ?? [];
    const hasInboundGroup = groups.includes('a-OktaSSO-APM0031544-EVIBAdmin-admins');
    const hasOutboundGroup = groups.includes('a-OktaSSO-APM0031544-EVOBAdmin-admins');

    if (this.selectedUserType === 'inbound' && (this.userAccessLevel === 'inboundAdmin' || hasInboundGroup)) return true;
    if (this.selectedUserType === 'outbound' && (this.userAccessLevel === 'outboundAdmin' || hasOutboundGroup)) return true;
    return false;
  }
  canShowReport(): boolean {
    const userType = localStorage.getItem('userType');
    if (userType === 'external') {
      return false;
    }
    return ['admin', 'inboundAdmin', 'outboundAdmin', 'inboundUser', 'outboundUser'].includes(this.userAccessLevel);
  }

  isActive(route: string): boolean {
    return this.currentRoute?.startsWith(route) ?? false;
  }


  selectNavLink(link: string): void {
    this.isDashboardSelect = false;
    this.isControlCenterSelect = false;
    this.isMasterDataSelects = false;
    this.isAdministratorRuleSelect = false;
    this.isReportSelect = false;

    if (link === 'dashboard') {
      this.isDashboardSelect = true;
    } else if (link === 'control-center') {
      this.isControlCenterSelect = true;
    } else if (link === 'master-data') {
      this.isMasterDataSelects = true;
    } else if (link === 'administrative-rule') {
      this.isAdministratorRuleSelect = true;
    } else if (link === 'app-report') {
      this.isReportSelect = true;
    }
  }
 
  highlightActiveLink() {
    const route = this.currentRoute || '';
    this.isDashboardSelect = route.includes('/dashboard');
    this.isControlCenterSelect = route.includes('/control-center');
    this.isMasterDataSelects = route.includes('/master-data');
    this.isAdministratorRuleSelect = route.includes('/administrative-rule');
  }

  isActiveMasterData(route: string) {
    return this.currentRoute?.startsWith(route) ?? false;
  }

  toggleProfileDropdown(): void {
    this.profileDropdownVisible = !this.profileDropdownVisible;
  }

  closeProfileDropdown(): void {
    setTimeout(() => this.profileDropdownVisible = false, 200);
  }

  logout(): void {
 
    this.apigeeAuthService.setToken('',0);
    this.authService.logout();
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.split(',');
    const first = parts[1]?.trim().charAt(0) || '';
    const last = parts[0]?.trim().charAt(0) || '';
    return (first + last).toUpperCase();
  }

}
